import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { motion } from "framer-motion";

/**
 * Saudi employers — bundled vectors in /public/companies/logos-sa/{id}.svg when present;
 * otherwise `<use>` resolves to /public/companies/companies-sprite.svg#id (placeholder glyphs).
 */
const SAUDI_SPRITE_IDS = [
  "aramco",
  "stc",
  "pif",
  "sabic",
  "neom",
  "saudia",
  "sec",
  "qiddiya",
  "ministry-of-finance",
  "elm",
  "alrajhi",
  "alinma",
  "tarshid",
  "kafd",
  "roshn",
  "marn",
  "site",
  "kaec",
  "floward",
  "tibah-airports",
] as const;

type SaudiSpriteId = (typeof SAUDI_SPRITE_IDS)[number];

/** Wikimedia Commons (or compatible) logos shipped in-repo; hyphen IDs match filenames. */
const SAUDI_LOGOS_FROM_FILES = new Set<SaudiSpriteId>([
  "aramco",
  "stc",
  "pif",
  "sabic",
  "saudia",
  "alrajhi",
]);

type MarqueeItem =
  | { kind: "sprite"; id: SaudiSpriteId; label: string }
  | { kind: "image"; id: string; src: string; label: string };

/** Global brands — existing SVGs under /public/trusted-logos (and LinkedIn under support-logos) */
const GLOBAL_LOGOS: readonly { id: string; src: string; labelKey: string }[] = [
  { id: "google", src: "/trusted-logos/google-1-1.svg", labelKey: "Google" },
  { id: "microsoft", src: "/trusted-logos/microsoft-6.svg", labelKey: "Microsoft" },
  { id: "amazon", src: "/trusted-logos/amazon.svg", labelKey: "Amazon" },
  { id: "apple", src: "/trusted-logos/apple-11.svg", labelKey: "Apple" },
  { id: "accenture", src: "/trusted-logos/accenture-2.svg", labelKey: "Accenture" },
  { id: "oracle", src: "/trusted-logos/oracle-logo.svg", labelKey: "Oracle" },
  { id: "kpmg", src: "/trusted-logos/kpmg.svg", labelKey: "KPMG" },
  { id: "linkedin", src: "/support-logos/linkedin.svg", labelKey: "LinkedIn" },
];

function labelForSaudiSprite(id: SaudiSpriteId): string {
  switch (id) {
    case "aramco":
      return t`Saudi Aramco`;
    case "stc":
      return t`stc`;
    case "pif":
      return t`Public Investment Fund`;
    case "sabic":
      return t`SABIC`;
    case "neom":
      return t`NEOM`;
    case "saudia":
      return t`Saudia`;
    case "sec":
      return t`Saudi Electricity Company`;
    case "qiddiya":
      return t`Qiddiya`;
    case "ministry-of-finance":
      return t`Ministry of Finance`;
    case "elm":
      return t`Elm`;
    case "alrajhi":
      return t`Al Rajhi Bank`;
    case "alinma":
      return t`Alinma Bank`;
    case "tarshid":
      return t`Tarshid`;
    case "kafd":
      return t`King Abdullah Financial District`;
    case "roshn":
      return t`Roshn`;
    case "marn":
      return t`Marn`;
    case "site":
      return t`SITE (Saudi Information Technology Company)`;
    case "kaec":
      return t`King Abdullah Economic City`;
    case "floward":
      return t`Floward`;
    case "tibah-airports":
      return t`Tibah Airports`;
    default: {
      const _exhaustive: never = id;
      return _exhaustive;
    }
  }
}

function labelForGlobalBrand(key: string): string {
  switch (key) {
    case "Google":
      return t`Google`;
    case "Microsoft":
      return t`Microsoft`;
    case "Amazon":
      return t`Amazon`;
    case "Apple":
      return t`Apple`;
    case "Accenture":
      return t`Accenture`;
    case "Oracle":
      return t`Oracle`;
    case "KPMG":
      return t`KPMG`;
    case "LinkedIn":
      return t`LinkedIn`;
    default:
      return key;
  }
}

export function EmployersMarqueeSection() {
  const { i18n } = useLingui();
  const isArabicLocale = i18n.locale === "ar-SA" || i18n.locale.startsWith("ar");

  const sectionTitle = isArabicLocale
    ? t`Our users work at Saudi Arabia's leading companies`
    : t`Our users work at leading global companies`;

  const trackItems: MarqueeItem[] = isArabicLocale
    ? SAUDI_SPRITE_IDS.map((id) => ({
        kind: "sprite",
        id,
        label: labelForSaudiSprite(id),
      }))
    : GLOBAL_LOGOS.map((g) => ({
        kind: "image",
        id: g.id,
        src: g.src,
        label: labelForGlobalBrand(g.labelKey),
      }));

  const duplicated: MarqueeItem[] = [...trackItems, ...trackItems];

  return (
    <section
      className="relative overflow-x-clip border-y border-border/50 bg-background py-16 sm:py-20"
      aria-labelledby="employers-marquee-heading"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

      <div className="container relative z-10 mx-auto mb-10 px-4 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          id="employers-marquee-heading"
          className="text-balance font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl"
        >
          {sectionTitle}
        </motion.h2>
      </div>

      {/* LTR track so motion matches your reference on RTL pages */}
      <div
        dir="ltr"
        className="relative [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]"
      >
        <div className="flex w-max gap-6 motion-safe:animate-scroll-marquee-smooth sm:gap-8 motion-reduce:animate-none [will-change:transform]">
          {duplicated.map((item, index) => (
            <div
              key={`${item.kind}-${item.id}-${index}`}
              className="group flex w-[7.5rem] shrink-0 cursor-default justify-center sm:w-[9rem]"
            >
              <div className="flex size-28 items-center justify-center rounded-lg bg-white/95 p-3 shadow-sm ring-1 ring-black/[0.06] transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-md sm:size-36 sm:rounded-xl sm:p-3.5">
                {item.kind === "sprite" ? (
                  SAUDI_LOGOS_FROM_FILES.has(item.id) ? (
                    <img
                      src={`/companies/logos-sa/${item.id}.svg`}
                      alt={item.label}
                      loading="lazy"
                      decoding="async"
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <svg
                      role="img"
                      aria-label={item.label}
                      className="size-full max-h-[4.5rem] w-full"
                      preserveAspectRatio="xMidYMid meet"
                    >
                      <title>{item.label}</title>
                      <use href={`/companies/companies-sprite.svg#${item.id}`} />
                    </svg>
                  )
                ) : (
                  <img
                    src={item.src}
                    alt={item.label}
                    loading="lazy"
                    decoding="async"
                    className="max-h-full max-w-full object-contain"
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
    </section>
  );
}
